import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Users, Calendar, CheckCircle2, 
  AlertCircle, Send, Eye, Printer, Mail, Building2,
  RefreshCw, AlertTriangle, Info, Search, Filter,
  ChevronRight, Lock, Unlock, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Checkbox } from '../../ui/checkbox';
import { Progress } from '../../ui/progress';
import { Separator } from '../../ui/separator';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { STATE_NAMES } from '../../../lib/usPayrollData';
import { formatDate } from '../../../lib/dateUtils';
import { generateW2PDF, generateW3PDF, generateW2cPDF } from '../../../lib/export';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const FORM_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  review: { label: 'Review', color: 'bg-amber-100 text-amber-700', icon: Eye },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  filed: { label: 'Filed', color: 'bg-emerald-100 text-emerald-700', icon: Send },
  distributed: { label: 'Distributed', color: 'bg-purple-100 text-purple-700', icon: Mail }
};

export default function YearEndForms() {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [w2Forms, setW2Forms] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForms, setSelectedForms] = useState([]);

  // Modal states
  const [showW2Preview, setShowW2Preview] = useState(false);
  const [selectedW2, setSelectedW2] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  
  // W-2c Correction states
  const [showW2cDialog, setShowW2cDialog] = useState(false);
  const [w2cData, setW2cData] = useState({
    originalW2: null,
    correctedW2: null,
    correctionReason: ''
  });
  const [w2cCorrections, setW2cCorrections] = useState([]);

  // Summary
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    formsGenerated: 0,
    formsApproved: 0,
    formsFiled: 0,
    totalWages: 0,
    totalFederalWithheld: 0,
    totalSocialSecurityWages: 0,
    totalSocialSecurity: 0,
    totalMedicareWages: 0,
    totalMedicare: 0,
    totalStateWages: 0,
    totalStateTax: 0
  });

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    try {
      const [users, payrollItems, existingForms, settings] = await Promise.all([
        db.users.where('role').notEqual('admin').toArray(),
        db.payrollRunItems.toArray(),
        db.yearEndForms.where('taxYear').equals(selectedYear).toArray(),
        db.companySettings.toArray()
      ]);

      // Filter payroll items for selected year
      const yearStart = new Date(selectedYear, 0, 1).getTime();
      const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59).getTime();
      const yearItems = payrollItems.filter(item => 
        item.createdAt >= yearStart && item.createdAt <= yearEnd
      );

      // Calculate YTD totals per employee
      const employeeYTD = {};
      for (const item of yearItems) {
        if (!employeeYTD[item.userId]) {
          employeeYTD[item.userId] = {
            grossWages: 0,
            federalTax: 0,
            socialSecurityWages: 0,
            socialSecurityTax: 0,
            medicareWages: 0,
            medicareTax: 0,
            stateTax: 0,
            localTax: 0,
            preTaxDeductions: 0
          };
        }
        employeeYTD[item.userId].grossWages += item.grossPay || 0;
        employeeYTD[item.userId].federalTax += item.federalTax || 0;
        employeeYTD[item.userId].socialSecurityWages += Math.min(item.grossPay || 0, 168600); // 2024 SS wage base
        employeeYTD[item.userId].socialSecurityTax += item.socialSecurity || 0;
        employeeYTD[item.userId].medicareWages += item.grossPay || 0;
        employeeYTD[item.userId].medicareTax += item.medicare || 0;
        employeeYTD[item.userId].stateTax += item.stateTax || 0;
        employeeYTD[item.userId].localTax += item.localTax || 0;
        employeeYTD[item.userId].preTaxDeductions += item.preTaxDeductions || 0;
      }

      // Merge employee data with YTD
      const enrichedEmployees = users
        .filter(u => employeeYTD[u.id])
        .map(user => ({
          ...user,
          ytd: employeeYTD[user.id],
          w2: existingForms.find(f => f.userId === user.id && f.formType === 'w2')
        }));

      setEmployees(enrichedEmployees);
      setW2Forms(existingForms.filter(f => f.formType === 'w2'));
      setCompanyInfo(settings[0] || null);

      // Calculate summary
      const totals = Object.values(employeeYTD).reduce((acc, emp) => ({
        totalWages: acc.totalWages + emp.grossWages - emp.preTaxDeductions,
        totalFederalWithheld: acc.totalFederalWithheld + emp.federalTax,
        totalSocialSecurityWages: acc.totalSocialSecurityWages + Math.min(emp.grossWages, 168600),
        totalSocialSecurity: acc.totalSocialSecurity + emp.socialSecurityTax,
        totalMedicareWages: acc.totalMedicareWages + emp.grossWages,
        totalMedicare: acc.totalMedicare + emp.medicareTax,
        totalStateWages: acc.totalStateWages + emp.grossWages,
        totalStateTax: acc.totalStateTax + emp.stateTax
      }), {
        totalWages: 0,
        totalFederalWithheld: 0,
        totalSocialSecurityWages: 0,
        totalSocialSecurity: 0,
        totalMedicareWages: 0,
        totalMedicare: 0,
        totalStateWages: 0,
        totalStateTax: 0
      });

      setSummary({
        totalEmployees: enrichedEmployees.length,
        formsGenerated: existingForms.filter(f => f.formType === 'w2').length,
        formsApproved: existingForms.filter(f => f.status === 'approved').length,
        formsFiled: existingForms.filter(f => f.status === 'filed').length,
        ...totals
      });

    } catch (error) {
      console.error('Error loading year-end data:', error);
      toast.error('Failed to load year-end forms data');
    } finally {
      setLoading(false);
    }
  };

  const generateW2Forms = async () => {
    setProcessing(true);

    try {
      const formsToCreate = [];
      const employeesToProcess = employees.filter(emp => !emp.w2 && emp.ytd);

      for (const emp of employeesToProcess) {
        // Get compensation data for work state
        const compensation = await db.employeeCompensation.where('userId').equals(emp.id).first();

        const w2Data = {
          id: uuidv4(),
          userId: emp.id,
          formType: 'w2',
          taxYear: selectedYear,
          status: 'draft',
          
          // Employee info
          employeeName: `${emp.profile?.firstName || ''} ${emp.profile?.lastName || ''}`,
          employeeSSN: emp.profile?.ssn || 'XXX-XX-XXXX',
          employeeAddress: emp.profile?.address || '',
          
          // Employer info
          employerEIN: companyInfo?.ein || '',
          employerName: companyInfo?.legalName || '',
          employerAddress: companyInfo?.address || '',

          // Box 1 - Wages, tips, other compensation
          box1_wages: emp.ytd.grossWages - emp.ytd.preTaxDeductions,
          
          // Box 2 - Federal income tax withheld
          box2_federalWithheld: emp.ytd.federalTax,
          
          // Box 3 - Social security wages
          box3_socialSecurityWages: Math.min(emp.ytd.grossWages, 168600),
          
          // Box 4 - Social security tax withheld
          box4_socialSecurityTax: emp.ytd.socialSecurityTax,
          
          // Box 5 - Medicare wages and tips
          box5_medicareWages: emp.ytd.grossWages,
          
          // Box 6 - Medicare tax withheld
          box6_medicareTax: emp.ytd.medicareTax,
          
          // Box 7 - Social security tips
          box7_socialSecurityTips: 0,
          
          // Box 8 - Allocated tips
          box8_allocatedTips: 0,
          
          // Box 10 - Dependent care benefits
          box10_dependentCare: 0,
          
          // Box 11 - Nonqualified plans
          box11_nonqualifiedPlans: 0,
          
          // Box 12 - Various codes
          box12_codes: [],
          
          // Box 13 - Checkboxes
          box13_statutory: false,
          box13_retirementPlan: false,
          box13_thirdPartySick: false,
          
          // Box 14 - Other
          box14_other: [],
          
          // State tax info (Box 15-17)
          stateInfo: [{
            stateCode: compensation?.workState || '',
            stateEIN: '',
            stateWages: emp.ytd.grossWages,
            stateTax: emp.ytd.stateTax
          }],
          
          // Local tax info (Box 18-20)
          localInfo: emp.ytd.localTax > 0 ? [{
            localityName: '',
            localWages: emp.ytd.grossWages,
            localTax: emp.ytd.localTax
          }] : [],

          createdAt: Date.now(),
          updatedAt: Date.now()
        };

        formsToCreate.push(w2Data);
      }

      if (formsToCreate.length > 0) {
        await db.yearEndForms.bulkAdd(formsToCreate);
        toast.success(`Generated ${formsToCreate.length} W-2 forms`);
      } else {
        toast.info('All W-2 forms already generated');
      }

      setShowGenerateDialog(false);
      loadData();

    } catch (error) {
      console.error('Error generating W-2 forms:', error);
      toast.error('Failed to generate W-2 forms');
    } finally {
      setProcessing(false);
    }
  };

  const updateFormStatus = async (formId, newStatus) => {
    try {
      await db.yearEndForms.update(formId, {
        status: newStatus,
        updatedAt: Date.now()
      });
      toast.success(`Form status updated to ${newStatus}`);
      loadData();
    } catch (error) {
      console.error('Error updating form status:', error);
      toast.error('Failed to update form status');
    }
  };

  const bulkUpdateStatus = async (newStatus) => {
    if (selectedForms.length === 0) {
      toast.error('Please select forms to update');
      return;
    }

    try {
      for (const formId of selectedForms) {
        await db.yearEndForms.update(formId, {
          status: newStatus,
          updatedAt: Date.now()
        });
      }
      toast.success(`Updated ${selectedForms.length} forms to ${newStatus}`);
      setSelectedForms([]);
      loadData();
    } catch (error) {
      console.error('Error bulk updating forms:', error);
      toast.error('Failed to update forms');
    }
  };

  const downloadW2 = (w2) => {
    if (!w2) return;
    
    try {
      // Map W-2 data to the format expected by generateW2PDF
      const w2Data = {
        ssn: w2.employeeSSN || 'XXX-XX-XXXX',
        ein: w2.employerEIN || 'XX-XXXXXXX',
        employerName: w2.employerName || 'PerformanceHub Inc.',
        employerAddress: typeof w2.employerAddress === 'object' 
          ? `${w2.employerAddress?.street || ''}, ${w2.employerAddress?.city || ''}, ${w2.employerAddress?.state || ''} ${w2.employerAddress?.zip || ''}` 
          : w2.employerAddress || '',
        employeeName: w2.employeeName || 'Employee',
        employeeAddress: typeof w2.employeeAddress === 'object'
          ? `${w2.employeeAddress?.street || ''}, ${w2.employeeAddress?.city || ''}, ${w2.employeeAddress?.state || ''} ${w2.employeeAddress?.zip || ''}`
          : w2.employeeAddress || '',
        wages: w2.box1_wages || 0,
        federalTax: w2.box2_federalWithheld || 0,
        socialSecurityWages: w2.box3_socialSecurityWages || 0,
        socialSecurityTax: w2.box4_socialSecurityTax || 0,
        medicareWages: w2.box5_medicareWages || 0,
        medicareTax: w2.box6_medicareTax || 0,
        socialSecurityTips: w2.box7_socialSecurityTips || 0,
        allocatedTips: w2.box8_allocatedTips || 0,
        stateCode: w2.stateInfo?.[0]?.stateCode || '',
        stateId: w2.stateInfo?.[0]?.stateEIN || '',
        stateWages: w2.stateInfo?.[0]?.stateWages || w2.box1_wages || 0,
        stateTax: w2.stateInfo?.[0]?.stateTax || 0,
        localWages: w2.localInfo?.[0]?.localWages || 0
      };
      
      generateW2PDF(w2Data, selectedYear);
      toast.success('W-2 PDF downloaded');
    } catch (error) {
      console.error('Error generating W-2 PDF:', error);
      toast.error('Error generating W-2 PDF');
    }
  };

  const downloadSelectedW2s = () => {
    if (selectedForms.length === 0) {
      toast.error('Please select forms to download');
      return;
    }

    const selectedW2Data = w2Forms.filter(f => selectedForms.includes(f.id));
    
    for (const w2 of selectedW2Data) {
      downloadW2(w2);
    }
    
    toast.success(`Downloaded ${selectedW2Data.length} W-2 forms`);
  };

  const toggleFormSelection = (formId) => {
    setSelectedForms(prev =>
      prev.includes(formId)
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  };

  const toggleAllForms = () => {
    if (selectedForms.length === w2Forms.length) {
      setSelectedForms([]);
    } else {
      setSelectedForms(w2Forms.map(f => f.id));
    }
  };

  const filteredEmployees = employees.filter(emp =>
    `${emp.profile?.firstName} ${emp.profile?.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Year-End Tax Forms</h1>
          <p className="text-gray-500 mt-1">Generate and manage W-2 and W-3 forms</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <SelectItem key={year} value={String(year)}>
                    Tax Year {year}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowGenerateDialog(true)}>
            <FileText className="w-4 h-4 mr-2" /> Generate W-2s
          </Button>
        </div>
      </div>

      {/* Important Dates Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Calendar className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Important Deadlines for Tax Year {selectedYear}</AlertTitle>
        <AlertDescription className="text-blue-700">
          <ul className="list-disc ml-4 mt-2 space-y-1">
            <li><strong>January 31, {selectedYear + 1}:</strong> W-2s must be delivered to employees</li>
            <li><strong>January 31, {selectedYear + 1}:</strong> W-2s and W-3 must be filed with SSA</li>
            <li><strong>February 28, {selectedYear + 1}:</strong> Paper filing deadline (if filing by mail)</li>
            <li><strong>March 31, {selectedYear + 1}:</strong> Electronic filing deadline</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Employees</p>
                <p className="text-2xl font-bold">{summary.totalEmployees}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">W-2s Generated</p>
                <p className="text-2xl font-bold">{summary.formsGenerated}</p>
              </div>
              <FileText className="w-8 h-8 text-amber-500" />
            </div>
            <Progress 
              value={(summary.formsGenerated / summary.totalEmployees) * 100} 
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Wages</p>
                <p className="text-2xl font-bold">{formatUSDCurrency(summary.totalWages)}</p>
              </div>
              <Building2 className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Federal Withheld</p>
                <p className="text-2xl font-bold text-red-600">{formatUSDCurrency(summary.totalFederalWithheld)}</p>
              </div>
              <Building2 className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="w2" className="space-y-4">
        <TabsList>
          <TabsTrigger value="w2">W-2 Forms</TabsTrigger>
          <TabsTrigger value="w3">W-3 Transmittal</TabsTrigger>
          <TabsTrigger value="corrections">Corrections (W-2c)</TabsTrigger>
        </TabsList>

        <TabsContent value="w2">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>W-2 Wage and Tax Statements</CardTitle>
                  <CardDescription>Individual employee W-2 forms for tax year {selectedYear}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedForms.length > 0 && (
                <div className="flex items-center gap-2 mb-4 p-2 bg-blue-50 rounded-lg">
                  <span className="text-sm text-blue-700">
                    {selectedForms.length} forms selected
                  </span>
                  <div className="flex gap-2 ml-auto">
                    <Button size="sm" variant="outline" onClick={() => bulkUpdateStatus('approved')}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => bulkUpdateStatus('filed')}>
                      <Send className="w-4 h-4 mr-1" /> Mark Filed
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.print()}>
                      <Printer className="w-4 h-4 mr-1" /> Print
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadSelectedW2s()}>
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </div>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedForms.length === w2Forms.length && w2Forms.length > 0}
                        onCheckedChange={toggleAllForms}
                      />
                    </TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Box 1 (Wages)</TableHead>
                    <TableHead className="text-right">Box 2 (Fed Tax)</TableHead>
                    <TableHead className="text-right">Box 3-6 (FICA)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No employees found for tax year {selectedYear}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map(emp => {
                      const w2 = emp.w2;
                      const StatusIcon = w2 ? FORM_STATUS[w2.status]?.icon : Clock;

                      return (
                        <TableRow key={emp.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedForms.includes(w2?.id)}
                              onCheckedChange={() => w2 && toggleFormSelection(w2.id)}
                              disabled={!w2}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 flex items-center justify-center text-white text-sm">
                                {emp.profile?.firstName?.[0]}{emp.profile?.lastName?.[0]}
                              </div>
                              <div>
                                <p className="font-medium">{emp.profile?.firstName} {emp.profile?.lastName}</p>
                                <p className="text-xs text-gray-500">{emp.profile?.ssn ? `***-**-${emp.profile.ssn.slice(-4)}` : 'No SSN'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatUSDCurrency(w2?.box1_wages || emp.ytd?.grossWages || 0)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {formatUSDCurrency(w2?.box2_federalWithheld || emp.ytd?.federalTax || 0)}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            <div>SS: {formatUSDCurrency(w2?.box4_socialSecurityTax || emp.ytd?.socialSecurityTax || 0)}</div>
                            <div>Med: {formatUSDCurrency(w2?.box6_medicareTax || emp.ytd?.medicareTax || 0)}</div>
                          </TableCell>
                          <TableCell>
                            {w2 ? (
                              <Badge className={FORM_STATUS[w2.status]?.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {FORM_STATUS[w2.status]?.label}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                Not Generated
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {w2 ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedW2(w2);
                                      setShowW2Preview(true);
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => downloadW2(w2)}>
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400">Generate W-2s first</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="w3">
          <Card>
            <CardHeader>
              <CardTitle>W-3 Transmittal of Wage and Tax Statements</CardTitle>
              <CardDescription>Summary transmittal form sent to SSA with W-2s for Tax Year {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* W-3 Form Preview */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                {/* Control Number and Kind of Payer */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-b pb-2">
                    <p className="text-xs text-gray-500">a Control number</p>
                    <p className="font-mono">33333</p>
                  </div>
                  <div className="border-b pb-2">
                    <p className="text-xs text-gray-500">b Kind of Payer (X one)</p>
                    <p className="font-medium">☒ 941 ☐ Military ☐ 943 ☐ 944 ☐ CT-1 ☐ Hshld emp.</p>
                  </div>
                </div>

                {/* Number of Forms and Establishment number */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-b pb-2">
                    <p className="text-xs text-gray-500">c Total number of Forms W-2</p>
                    <p className="text-xl font-bold">{summary.formsGenerated}</p>
                  </div>
                  <div className="border-b pb-2">
                    <p className="text-xs text-gray-500">d Establishment number</p>
                    <p className="font-mono">-</p>
                  </div>
                </div>

                {/* Employer Identification */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-b pb-2">
                    <p className="text-xs text-gray-500">e Employer identification number (EIN)</p>
                    <p className="font-mono font-medium">{companyInfo?.ein || 'XX-XXXXXXX'}</p>
                  </div>
                  <div className="border-b pb-2">
                    <p className="text-xs text-gray-500">f Employer's name</p>
                    <p className="font-medium">{companyInfo?.legalName || 'Company Name'}</p>
                    <p className="text-sm text-gray-600">
                      {typeof companyInfo?.address === 'object' && companyInfo?.address
                        ? `${companyInfo.address.street || ''}`
                        : (companyInfo?.address || 'Address')}
                    </p>
                    <p className="text-sm text-gray-600">
                      {typeof companyInfo?.address === 'object' && companyInfo?.address
                        ? `${companyInfo.address.city || ''}, ${companyInfo.address.state || ''} ${companyInfo.address.zip || ''}`
                        : `${companyInfo?.city || ''}, ${companyInfo?.state || ''} ${companyInfo?.zip || ''}`}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Wage and Tax Boxes */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">1 Wages, tips, other compensation</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalWages)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">2 Federal income tax withheld</p>
                    <p className="text-lg font-bold text-red-600">{formatUSDCurrency(summary.totalFederalWithheld)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">3 Social security wages</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalSocialSecurityWages)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">4 Social security tax withheld</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalSocialSecurity)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">5 Medicare wages and tips</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalMedicareWages)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">6 Medicare tax withheld</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalMedicare)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">7 Social security tips</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(0)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">8 Allocated tips</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(0)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">9 (Blank)</p>
                    <p className="text-lg font-bold">-</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">10 Dependent care benefits</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(0)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">11 Nonqualified plans</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(0)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">12a Deferred compensation</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(0)}</p>
                  </div>
                </div>

                <Separator />

                {/* State/Local Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">15 State / Employer's state ID number</p>
                    <p className="font-medium">{companyInfo?.stateEIN || '-'}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">16 State wages, tips, etc.</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalStateWages)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">17 State income tax</p>
                    <p className="text-lg font-bold">{formatUSDCurrency(summary.totalStateTax)}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">18-19 Local wages / Local income tax</p>
                    <p className="text-lg font-bold">-</p>
                  </div>
                </div>

                <Separator />

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Contact person</p>
                    <p className="font-medium">{companyInfo?.contactName || 'Administrator'}</p>
                    <p className="text-sm text-gray-600">Phone: {companyInfo?.phone || '-'}</p>
                    <p className="text-sm text-gray-600">Email: {companyInfo?.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Tax Year</p>
                    <p className="text-2xl font-bold text-teal-600">{selectedYear}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => {
                    toast.info('W-3 preview will open in a new window');
                    window.print();
                  }}>
                    <Eye className="w-4 h-4 mr-2" /> Preview W-3
                  </Button>
                  <Button variant="outline" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" /> Print W-3
                  </Button>
                  <Button onClick={() => {
                    if (summary.formsGenerated === 0) {
                      toast.error('Generate W-2 forms first before filing W-3');
                      return;
                    }
                    toast.success('W-3 ready for SSA filing. Visit ssa.gov/bso for electronic submission.');
                  }}>
                    <Send className="w-4 h-4 mr-2" /> File with SSA
                  </Button>
                  <Button variant="outline" onClick={() => {
                    try {
                      generateW3PDF(summary, companyInfo, selectedYear);
                      toast.success('W-3 PDF downloaded');
                    } catch (error) {
                      console.error('Error generating W-3 PDF:', error);
                      toast.error('Error generating W-3 PDF');
                    }
                  }}>
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                </div>
              </div>

              {/* Filing Instructions */}
              <div className="mt-6 p-4 border rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  Filing Instructions
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• File Form W-3 transmittal with Copy A of all W-2s</li>
                  <li>• Submit to Social Security Administration by January 31</li>
                  <li>• Electronic filing is required if filing 250 or more W-2s</li>
                  <li>• Use Business Services Online (BSO) at ssa.gov for electronic filing</li>
                  <li>• Keep copies of W-2s and W-3 for at least 4 years</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="corrections">
          <Card>
            <CardHeader>
              <CardTitle>W-2c Corrections</CardTitle>
              <CardDescription>Correct errors on previously filed W-2 forms</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="bg-amber-50 border-amber-200 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">When to File W-2c</AlertTitle>
                <AlertDescription className="text-amber-700">
                  File Form W-2c to correct errors on a W-2 after you discover them. Common corrections include:
                  incorrect SSN, incorrect name spelling, or incorrect wage/tax amounts.
                </AlertDescription>
              </Alert>

              {/* W-2 Forms that can be corrected */}
              {w2Forms.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="font-medium">Select a W-2 to Correct</h4>
                  <div className="grid gap-2 max-h-60 overflow-y-auto">
                    {w2Forms.map(w2 => (
                      <div
                        key={w2.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setW2cData({
                            originalW2: {
                              employeeName: w2.employeeName,
                              ssn: w2.employeeSSN,
                              ein: w2.employerEIN,
                              employerName: w2.employerName,
                              wages: w2.box1_wages || 0,
                              federalTax: w2.box2_federalWithheld || 0,
                              socialSecurityWages: w2.box3_socialSecurityWages || 0,
                              socialSecurityTax: w2.box4_socialSecurityTax || 0,
                              medicareWages: w2.box5_medicareWages || 0,
                              medicareTax: w2.box6_medicareTax || 0,
                              stateWages: w2.stateInfo?.[0]?.stateWages || 0,
                              stateTax: w2.stateInfo?.[0]?.stateTax || 0
                            },
                            correctedW2: {
                              employeeName: w2.employeeName,
                              ssn: w2.employeeSSN,
                              ein: w2.employerEIN,
                              employerName: w2.employerName,
                              wages: w2.box1_wages || 0,
                              federalTax: w2.box2_federalWithheld || 0,
                              socialSecurityWages: w2.box3_socialSecurityWages || 0,
                              socialSecurityTax: w2.box4_socialSecurityTax || 0,
                              medicareWages: w2.box5_medicareWages || 0,
                              medicareTax: w2.box6_medicareTax || 0,
                              stateWages: w2.stateInfo?.[0]?.stateWages || 0,
                              stateTax: w2.stateInfo?.[0]?.stateTax || 0
                            },
                            correctionReason: ''
                          });
                          setShowW2cDialog(true);
                        }}
                      >
                        <div>
                          <p className="font-medium">{w2.employeeName}</p>
                          <p className="text-sm text-gray-500">
                            Wages: {formatUSDCurrency(w2.box1_wages)} | Fed Tax: {formatUSDCurrency(w2.box2_federalWithheld)}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <FileText className="w-4 h-4 mr-1" /> Create W-2c
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Previous Corrections */}
                  {w2cCorrections.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium mb-2">Previous Corrections</h4>
                      <div className="space-y-2">
                        {w2cCorrections.map((correction, idx) => (
                          <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="font-medium">{correction.employeeName}</p>
                              <p className="text-sm text-gray-600">{correction.reason}</p>
                            </div>
                            <Badge className="bg-red-100 text-red-700">Corrected</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Generate W-2 forms first to create corrections</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* W-2c Correction Dialog */}
      <Dialog open={showW2cDialog} onOpenChange={setShowW2cDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Create W-2c Correction
            </DialogTitle>
            <DialogDescription>
              Enter the corrected values for {w2cData.originalW2?.employeeName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Reason for Correction *</Label>
              <Input
                placeholder="e.g., Incorrect wage amount, SSN correction, etc."
                value={w2cData.correctionReason}
                onChange={(e) => setW2cData(prev => ({ ...prev, correctionReason: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm font-medium text-gray-500 border-b pb-2">
              <div>Box</div>
              <div>Previously Reported</div>
              <div>Corrected Amount</div>
            </div>
            
            {[
              { key: 'wages', label: 'Box 1 - Wages' },
              { key: 'federalTax', label: 'Box 2 - Federal Tax' },
              { key: 'socialSecurityWages', label: 'Box 3 - SS Wages' },
              { key: 'socialSecurityTax', label: 'Box 4 - SS Tax' },
              { key: 'medicareWages', label: 'Box 5 - Medicare Wages' },
              { key: 'medicareTax', label: 'Box 6 - Medicare Tax' },
              { key: 'stateWages', label: 'Box 16 - State Wages' },
              { key: 'stateTax', label: 'Box 17 - State Tax' }
            ].map(({ key, label }) => (
              <div key={key} className="grid grid-cols-3 gap-4 items-center">
                <div className="text-sm">{label}</div>
                <div className="text-sm text-gray-600">
                  {formatUSDCurrency(w2cData.originalW2?.[key] || 0)}
                </div>
                <Input
                  type="number"
                  step="0.01"
                  value={w2cData.correctedW2?.[key] || 0}
                  onChange={(e) => setW2cData(prev => ({
                    ...prev,
                    correctedW2: { ...prev.correctedW2, [key]: parseFloat(e.target.value) || 0 }
                  }))}
                  className={w2cData.correctedW2?.[key] !== w2cData.originalW2?.[key] ? 'border-amber-400 bg-amber-50' : ''}
                />
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowW2cDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!w2cData.correctionReason) {
                  toast.error('Please enter a reason for correction');
                  return;
                }
                try {
                  generateW2cPDF(w2cData.originalW2, w2cData.correctedW2, selectedYear, w2cData.correctionReason);
                  setW2cCorrections(prev => [...prev, {
                    employeeName: w2cData.originalW2?.employeeName,
                    reason: w2cData.correctionReason,
                    date: new Date().toISOString()
                  }]);
                  setShowW2cDialog(false);
                  toast.success('W-2c correction PDF generated');
                } catch (error) {
                  console.error('Error generating W-2c:', error);
                  toast.error('Error generating W-2c');
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Download className="w-4 h-4 mr-2" /> Generate W-2c PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate W-2s Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate W-2 Forms</DialogTitle>
            <DialogDescription>
              Generate W-2 forms for all employees for tax year {selectedYear}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                This will generate W-2 forms for {employees.filter(e => !e.w2).length} employees 
                who don't already have forms.
              </AlertDescription>
            </Alert>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Employees:</span>
                  <span>{summary.totalEmployees}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Already Generated:</span>
                  <span>{summary.formsGenerated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">To Generate:</span>
                  <span className="font-medium">{summary.totalEmployees - summary.formsGenerated}</span>
                </div>
              </div>
            </div>

            {!companyInfo?.ein && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Please configure your company's EIN in Tax Settings before generating W-2s.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateW2Forms} 
              disabled={processing || !companyInfo?.ein}
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" /> Generate W-2s</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* W-2 Preview Modal */}
      <Dialog open={showW2Preview} onOpenChange={setShowW2Preview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>W-2 Preview</DialogTitle>
            <DialogDescription>
              Tax Year {selectedYear} - {selectedW2?.employeeName}
            </DialogDescription>
          </DialogHeader>

          {selectedW2 && (
            <div className="border-2 border-gray-300 rounded-lg p-6 bg-white">
              {/* W-2 Form Layout */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {/* Left Column */}
                <div className="space-y-3">
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">a Employee's SSN</p>
                    <p className="font-mono">{selectedW2.employeeSSN || 'XXX-XX-XXXX'}</p>
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">b Employer ID (EIN)</p>
                    <p className="font-mono">{selectedW2.employerEIN || 'XX-XXXXXXX'}</p>
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">c Employer's name, address</p>
                    <p>{selectedW2.employerName || 'Company Name'}</p>
                    <p className="text-gray-600">
                      {typeof selectedW2.employerAddress === 'object' && selectedW2.employerAddress
                        ? `${selectedW2.employerAddress.street || ''}, ${selectedW2.employerAddress.city || ''}, ${selectedW2.employerAddress.state || ''} ${selectedW2.employerAddress.zip || ''}`
                        : (selectedW2.employerAddress || 'Address')}
                    </p>
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">e Employee's name</p>
                    <p className="font-medium">{selectedW2.employeeName || 'Employee Name'}</p>
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">f Employee's address</p>
                    <p>
                      {typeof selectedW2.employeeAddress === 'object' && selectedW2.employeeAddress
                        ? `${selectedW2.employeeAddress.street || ''}, ${selectedW2.employeeAddress.city || ''}, ${selectedW2.employeeAddress.state || ''} ${selectedW2.employeeAddress.zip || ''}`
                        : (selectedW2.employeeAddress || 'Address on file')}
                    </p>
                  </div>
                </div>

                {/* Middle Column - Boxes 1-8 */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">1 Wages, tips, other comp</p>
                      <p className="font-bold">{formatUSDCurrency(selectedW2.box1_wages || 0)}</p>
                    </div>
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">2 Federal income tax withheld</p>
                      <p className="font-bold text-red-600">{formatUSDCurrency(selectedW2.box2_federalWithheld || 0)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">3 Social security wages</p>
                      <p>{formatUSDCurrency(selectedW2.box3_socialSecurityWages || 0)}</p>
                    </div>
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">4 Social security tax withheld</p>
                      <p>{formatUSDCurrency(selectedW2.box4_socialSecurityTax || 0)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">5 Medicare wages and tips</p>
                      <p>{formatUSDCurrency(selectedW2.box5_medicareWages || 0)}</p>
                    </div>
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">6 Medicare tax withheld</p>
                      <p>{formatUSDCurrency(selectedW2.box6_medicareTax || 0)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">7 Social security tips</p>
                      <p>{formatUSDCurrency(selectedW2.box7_socialSecurityTips || 0)}</p>
                    </div>
                    <div className="border p-2">
                      <p className="text-xs text-gray-500">8 Allocated tips</p>
                      <p>{formatUSDCurrency(selectedW2.box8_allocatedTips || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - State/Local */}
                <div className="space-y-3">
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">15 State / Employer's state ID</p>
                    {selectedW2.stateInfo && selectedW2.stateInfo.length > 0 ? (
                      selectedW2.stateInfo.map((state, i) => (
                        <p key={i}>{state.stateCode || '-'} / {state.stateEIN || '-'}</p>
                      ))
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">16 State wages, tips, etc.</p>
                    {selectedW2.stateInfo && selectedW2.stateInfo.length > 0 ? (
                      selectedW2.stateInfo.map((state, i) => (
                        <p key={i}>{formatUSDCurrency(state.stateWages || 0)}</p>
                      ))
                    ) : (
                      <p>{formatUSDCurrency(0)}</p>
                    )}
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">17 State income tax</p>
                    {selectedW2.stateInfo && selectedW2.stateInfo.length > 0 ? (
                      selectedW2.stateInfo.map((state, i) => (
                        <p key={i}>{formatUSDCurrency(state.stateTax || 0)}</p>
                      ))
                    ) : (
                      <p>{formatUSDCurrency(0)}</p>
                    )}
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">18 Local wages, tips, etc.</p>
                    {selectedW2.localInfo && selectedW2.localInfo.length > 0 ? (
                      selectedW2.localInfo.map((local, i) => (
                        <p key={i}>{formatUSDCurrency(local.localWages || 0)}</p>
                      ))
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">19 Local income tax</p>
                    {selectedW2.localInfo && selectedW2.localInfo.length > 0 ? (
                      selectedW2.localInfo.map((local, i) => (
                        <p key={i}>{formatUSDCurrency(local.localTax || 0)}</p>
                      ))
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                  <div className="border p-2">
                    <p className="text-xs text-gray-500">20 Locality name</p>
                    {selectedW2.localInfo && selectedW2.localInfo.length > 0 ? (
                      selectedW2.localInfo.map((local, i) => (
                        <p key={i}>{local.localityName || '-'}</p>
                      ))
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center text-xs text-gray-400">
                Form W-2 Wage and Tax Statement - Tax Year {selectedYear}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowW2Preview(false)}>
              Close
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button onClick={() => downloadW2(selectedW2)}>
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
